import { useEffect, useState } from "react";
import {
  fetchCommissionModelerCopy,
  type CommissionModelerFormCopy,
} from "../../api/commissionModelerCopy";
import { COMMISSION_MODELER_COPY_FALLBACK } from "./copy";

/**
 * The modeler form's labels and helper text, resolved from the server's PUBLISHED
 * copy and overlaid on the compiled-in defaults.
 *
 * **Never pending, never partial.** It starts at {@link COMMISSION_MODELER_COPY_FALLBACK}
 * — the same words the tab shipped with — and swaps to the server's payload only
 * once a whole payload arrives. There is deliberately no `loading` state and nothing
 * for a caller to branch on: the alternative is a form that paints inputs with no
 * labels for a frame, or worse, blocks on a copy fetch. An empty override table
 * resolves to the same words as the fallback, so a store nobody has published to is
 * byte-identical to today.
 *
 * Direct clone of `useFunnelCopyResolution` (portal/pages/prequalify/
 * PrequalBrandContext.tsx), minus the context: the funnel resolves once for a whole
 * multi-route flow, while this is one tab, so a provider would be ceremony around a
 * single caller. The fetcher swallows every failure, so there is no catch arm here
 * to get wrong.
 *
 * Only the FORM half is returned. The response also carries the report's disclosure
 * paragraphs — for a future "here is what your client will read" preview — and a
 * caller wanting those should read them from `fetchCommissionModelerCopy` rather
 * than have this hook hand back a shape whose other half nothing renders.
 */
export function useCommissionModelerCopy(): CommissionModelerFormCopy {
  const [copy, setCopy] = useState<CommissionModelerFormCopy | null>(null);

  useEffect(() => {
    let alive = true;
    void fetchCommissionModelerCopy().then((resolved) => {
      // A null resolve is any failure at all. Keep the fallback; say nothing.
      if (alive && resolved) setCopy(resolved.form);
    });
    return () => {
      alive = false;
    };
  }, []);

  return copy ?? COMMISSION_MODELER_COPY_FALLBACK;
}
